/**
 * Rank unfilled roster shifts by CONSEQUENCE, not by start time.
 *
 * WHY THIS EXISTS. The board currently sorts gaps by start time and hardcodes
 * `criticalUnfilledCount={0}` on the residential dispatch header. An unfilled
 * RN night shift and an unfilled laundry shift look identical; the header's
 * "affecting compliance" line never lights.
 *
 * Production, Medea Park, 2026-08-03: 196 unfilled, of which 178 have already
 * ended. Of the 18 actionable, ten are RN-family. Sorting the undivided set by
 * time buries the work list under history and puts hotel-services gaps on equal
 * footing with a 24/7 nursing obligation.
 *
 * THE ORDER (highest first; lapsed is excluded from the work list):
 *   1. BREAKS_RN_COVER — filling would cover hour(s) currently without any
 *      ASSIGNED RN. An unfilled RN shift at a time when another RN is assigned
 *      does not break cover and must not receive this rank.
 *   2. UNDER_CARE_MINUTE_TARGET — the day is below its care-minute target and
 *      this shift would contribute care minutes. ONLY when a genuine assessed
 *      target exists (`careMinuteTargetsForCoverageSummary`); provisional or
 *      unit-inferred targets never produce this rank.
 *   3. CREDENTIAL_REQUIREMENT_ABSENT — care role (RN / EN / PCA) with no
 *      required_classification. Cannot be filled safely by matching alone.
 *   4. OPERATIONAL — everything else.
 *
 * REUSES, does not reimplement:
 *   - `classifyUnfilledUrgency` / `countUnfilledByUrgency` for the time split
 *   - `classifyRosterService` / `normaliseServiceToken` for role family
 *   - `rnCoverageGapHours` for the evidence-aware planned RN cover picture
 *   - `careMinuteTargetsForCoverageSummary` + `buildCareCoverageSummary` for
 *     the care-minute gate
 */

import {
  careMinuteTargetsForCoverageSummary,
  type ResolvedCareMinuteTarget,
} from './care-minute-target-resolver';
import { buildCareCoverageSummary, rnCoverageGapHours } from './roster-care-coverage';
import {
  classifyRosterService,
  normaliseServiceToken,
  type RosterServiceFamily,
} from './roster-service-classification';
import {
  classifyUnfilledUrgency,
  countUnfilledByUrgency,
  type UnfilledUrgency,
  type UnfilledUrgencyCounts,
} from './roster-unfilled-urgency';

// ── Public types ───────────────────────────────────────────────────────────

/**
 * Work-list ranks only. Lapsed shifts are excluded entirely and reported in
 * the result separately — they are not a fifth rank on the queue.
 */
export type UnfilledConsequenceRank =
  | 'BREAKS_RN_COVER'
  | 'UNDER_CARE_MINUTE_TARGET'
  | 'CREDENTIAL_REQUIREMENT_ABSENT'
  | 'OPERATIONAL';

/** Numeric severity for sorting: lower = more urgent. */
export const UNFILLED_CONSEQUENCE_RANK_ORDER: Readonly<Record<UnfilledConsequenceRank, number>> = {
  BREAKS_RN_COVER: 1,
  UNDER_CARE_MINUTE_TARGET: 2,
  CREDENTIAL_REQUIREMENT_ABSENT: 3,
  OPERATIONAL: 4,
} as const;

/**
 * Minimum fields the ranker needs from an unfilled shift. Callers may pass a
 * richer row; the extra fields are preserved on the ranked result.
 */
export type UnfilledShiftForConsequence = {
  readonly id: string;
  readonly startMs: number;
  readonly endMs: number;
  readonly serviceType?: string | null;
  readonly serviceCode?: string | null;
  /**
   * Stored classification requirement. Null/empty is the Medea-shaped gap:
   * 73% of shifts carry none, including hundreds of RN/EN rows.
   */
  readonly requiredClassification?: string | null;
  /**
   * Local calendar date (YYYY-MM-DD in tenant TZ) for the care-minute day
   * picture. Required for rank 2; without it that rank cannot fire.
   */
  readonly localDate?: string | null;
};

/** Half-open local day used to compute RN gap hours via `rnCoverageGapHours`. */
export type RnCoverDayWindow = {
  readonly dayStartMs: number;
  /** Exclusive. */
  readonly dayEndMs: number;
};

/**
 * Per-day rostered care-minute picture (ordinary unfilled shifts not counted).
 * Rank 2 grades this against an assessable target only.
 */
export type CareMinuteDayPicture = {
  readonly localDate: string;
  readonly rosteredCareMinutes: number;
  readonly rosteredRnMinutes: number;
  readonly occupiedBeds: number;
};

export type RankUnfilledByConsequenceInput<T extends UnfilledShiftForConsequence> = {
  readonly unfilledShifts: ReadonlyArray<T>;
  readonly nowMs: number;
  /**
   * RN intervals with evidence-aware planned cover. Ordinary unfilled RN rows
   * must not appear here. Provider-backed agency RN cover may appear, but the
   * caller must retain its separate supplier-confirmation advisory.
   */
  readonly assignedRnIntervals: ReadonlyArray<{
    readonly startMs: number;
    readonly endMs: number;
  }>;
  /**
   * Local day windows covering the period. Gaps are computed per day then
   * unioned so multi-day periods (Medea's RN gaps run to 30 Aug) stay correct
   * on DST-length days when the caller supplies true day bounds.
   */
  readonly rnCoverDays: ReadonlyArray<RnCoverDayWindow>;
  /**
   * Facility target resolution for the period. Rank 2 is gated through
   * `careMinuteTargetsForCoverageSummary` — PROVISIONAL and unit-inferred
   * resolutions yield no care-minute rank.
   */
  readonly careMinuteTargetResolution: ResolvedCareMinuteTarget;
  /**
   * Assigned care minutes per local day. Omit or leave empty when the day
   * picture is unknown — rank 2 simply cannot fire.
   */
  readonly careMinuteDays?: ReadonlyArray<CareMinuteDayPicture>;
};

export type RankedUnfilledShift<T extends UnfilledShiftForConsequence> = {
  readonly shift: T;
  readonly rank: UnfilledConsequenceRank;
  /** Short human reason for the board / drawer. */
  readonly reason: string;
  /** Time urgency of the gap (never LAPSED — those are excluded). */
  readonly urgency: Extract<UnfilledUrgency, 'IN_PROGRESS' | 'UPCOMING'>;
};

export type UnfilledConsequenceCounts = {
  readonly breaksRnCover: number;
  readonly underCareMinuteTarget: number;
  readonly credentialRequirementAbsent: number;
  readonly operational: number;
};

export type RankUnfilledByConsequenceResult<T extends UnfilledShiftForConsequence> = {
  /** Actionable gaps, highest consequence first, start time as tie-break. */
  readonly ranked: ReadonlyArray<RankedUnfilledShift<T>>;
  /** Ended with nobody assigned — historical record, not a task. */
  readonly lapsed: ReadonlyArray<T>;
  readonly urgencyCounts: UnfilledUrgencyCounts;
  readonly countsByRank: UnfilledConsequenceCounts;
  /**
   * Ranks 1-2 — the gaps that carry a REGULATORY consequence. What
   * DispatchSurface passes as `criticalUnfilledCount` instead of the hardcoded
   * zero. Credential gaps are excluded: they sort high because the shift cannot
   * be filled safely by matching, but they breach nothing.
   */
  readonly affectingComplianceCount: number;
};

// ── Role helpers (thin wrappers over classifyRosterService) ────────────────

const CARE_ROLE_FAMILIES: ReadonlySet<RosterServiceFamily> = new Set(['NURSING', 'PERSONAL_CARE']);

/** Families whose filled minutes typically count toward care-minute totals. */
const CARE_MINUTE_FAMILIES: ReadonlySet<RosterServiceFamily> = new Set([
  'NURSING',
  'PERSONAL_CARE',
  'CLINICAL',
  'CARE_ROUND',
]);

function serviceFamilyOf(shift: UnfilledShiftForConsequence): RosterServiceFamily {
  return classifyRosterService({
    serviceCode: shift.serviceCode,
    serviceType: shift.serviceType,
  });
}

function serviceTokenOf(shift: UnfilledShiftForConsequence): string {
  return normaliseServiceToken(shift.serviceCode) || normaliseServiceToken(shift.serviceType);
}

/**
 * True when filling this shift would put a registered nurse on the floor.
 *
 * Uses the display family for the broad nursing bucket, then excludes EN
 * tokens — an enrolled nurse is nursing work but does not satisfy the RN 24/7
 * condition of operating. Does not invent a second free-text parser: tokens
 * come from `normaliseServiceToken`, the same normaliser the board colours use.
 */
export function isRnCoverRole(shift: UnfilledShiftForConsequence): boolean {
  if (serviceFamilyOf(shift) !== 'NURSING') return false;
  const token = serviceTokenOf(shift);
  if (!token) return false;
  // Explicit EN / enrolled tokens are nursing, not RN cover.
  if (
    token === 'EN' ||
    token.startsWith('EN_') ||
    token.includes('ENROLLED') ||
    token === 'ENROLLED_NURSE'
  ) {
    return false;
  }
  // Known RN-family tokens from live Medea + sibling residential free text.
  if (
    token === 'RN' ||
    token.startsWith('RN_') ||
    token.startsWith('LEAD_RN') ||
    token.startsWith('LRN') ||
    token.includes('REGISTERED_NURSE') ||
    token === 'NURSING' ||
    token === 'NURSE' ||
    // Medication rounds and clinical nursing labels still put an RN on site
    // when the family is already NURSING and not EN.
    token.includes('MEDICATION')
  ) {
    return true;
  }
  // Remaining NURSING family values (e.g. free-text "Registered nurse night")
  // after EN exclusion: treat as RN cover. Safer to over-flag a rare gloss
  // than to miss a real RN gap because the token was unfamiliar.
  return true;
}

export function isCareCredentialRole(shift: UnfilledShiftForConsequence): boolean {
  return CARE_ROLE_FAMILIES.has(serviceFamilyOf(shift));
}

function contributesCareMinutes(shift: UnfilledShiftForConsequence): boolean {
  return CARE_MINUTE_FAMILIES.has(serviceFamilyOf(shift));
}

function hasRequiredClassification(shift: UnfilledShiftForConsequence): boolean {
  const value = shift.requiredClassification;
  if (value == null) return false;
  return String(value).trim().length > 0;
}

// ── RN gap overlap ─────────────────────────────────────────────────────────

/**
 * Hour starts currently without planned RN cover, across every cover day.
 * Half-open hour semantics match `rnCoverageGapHours`.
 */
function collectRnGapHourStarts(
  rnCoverDays: ReadonlyArray<RnCoverDayWindow>,
  assignedRnIntervals: ReadonlyArray<{ readonly startMs: number; readonly endMs: number }>,
): number[] {
  const gaps: number[] = [];
  for (const day of rnCoverDays) {
    if (!Number.isFinite(day.dayStartMs) || !Number.isFinite(day.dayEndMs)) continue;
    if (day.dayEndMs <= day.dayStartMs) continue;
    const dayGaps = rnCoverageGapHours({
      dayStartMs: day.dayStartMs,
      dayEndMs: day.dayEndMs,
      assignedRnIntervals,
    });
    for (const hourStart of dayGaps) gaps.push(hourStart);
  }
  return gaps;
}

/**
 * True when the shift spans at least one hour that currently has no assigned
 * RN. Filling the shift would change RN cover for that hour.
 *
 * An unfilled RN shift wholly inside already-covered hours returns false —
 * another RN is already assigned, so leaving this one open does not break
 * the 24/7 condition. That is the critical correctness point for rank 1.
 */
export function shiftOverlapsRnCoverageGap(
  shift: { readonly startMs: number; readonly endMs: number },
  rnGapHourStarts: ReadonlyArray<number>,
): boolean {
  if (!Number.isFinite(shift.startMs) || !Number.isFinite(shift.endMs)) return false;
  if (shift.endMs <= shift.startMs) return false;
  for (const hourStart of rnGapHourStarts) {
    // Same half-open rule as rnCoverageGapHours: interval covers hourStart
    // when start <= hourStart < end.
    if (shift.startMs <= hourStart && shift.endMs > hourStart) return true;
  }
  return false;
}

// ── Care-minute under-target days ──────────────────────────────────────────

/**
 * Local dates whose assigned care minutes grade as UNDER against an assessable
 * target. Empty when the resolution is provisional, unit-inferred, missing, or
 * inconsistent — rank 2 must not invent a regulatory consequence.
 */
function underCareMinuteTargetDates(
  resolution: ResolvedCareMinuteTarget,
  careMinuteDays: ReadonlyArray<CareMinuteDayPicture> | undefined,
): ReadonlySet<string> {
  const under = new Set<string>();
  if (!careMinuteDays || careMinuteDays.length === 0) return under;

  const targets = careMinuteTargetsForCoverageSummary(resolution);
  if (targets.targetTotalMinutesPerResident == null || targets.targetRnMinutesPerResident == null) {
    return under;
  }

  for (const day of careMinuteDays) {
    const summary = buildCareCoverageSummary({
      rosteredCareMinutes: day.rosteredCareMinutes,
      rosteredRnMinutes: day.rosteredRnMinutes,
      occupiedBeds: day.occupiedBeds,
      targetTotalMinutesPerResident: targets.targetTotalMinutesPerResident,
      targetRnMinutesPerResident: targets.targetRnMinutesPerResident,
    });
    // "Below the care-minute target" — UNDER only. AT_RISK is at-or-above
    // the minimum with no buffer; it is not a fabricated breach, but it is
    // also not "below", so it does not light rank 2.
    if (summary.total.status === 'UNDER' || summary.rn.status === 'UNDER') {
      under.add(day.localDate);
    }
  }
  return under;
}

// ── Reasons ────────────────────────────────────────────────────────────────

const REASON_BY_RANK: Readonly<Record<UnfilledConsequenceRank, string>> = {
  BREAKS_RN_COVER: 'No RN assigned for these hours',
  UNDER_CARE_MINUTE_TARGET: 'Day is below care-minute target',
  CREDENTIAL_REQUIREMENT_ABSENT: 'Care role with no classification requirement',
  OPERATIONAL: 'Operational gap',
};

export function unfilledConsequenceReason(rank: UnfilledConsequenceRank): string {
  return REASON_BY_RANK[rank];
}

// ── Classify one shift ─────────────────────────────────────────────────────

function classifyConsequenceRank(
  shift: UnfilledShiftForConsequence,
  rnGapHourStarts: ReadonlyArray<number>,
  underTargetDates: ReadonlySet<string>,
): UnfilledConsequenceRank {
  // 1. RN cover: only RN-capable roles, and only when the hours they span are
  //    currently uncovered by any assigned RN.
  if (isRnCoverRole(shift) && shiftOverlapsRnCoverageGap(shift, rnGapHourStarts)) {
    return 'BREAKS_RN_COVER';
  }

  // 2. Care-minute shortfall — assessable target + under day + care role.
  const localDate = shift.localDate?.trim() ?? '';
  if (localDate && underTargetDates.has(localDate) && contributesCareMinutes(shift)) {
    return 'UNDER_CARE_MINUTE_TARGET';
  }

  // 3. Care role with no credential requirement on the row.
  if (isCareCredentialRole(shift) && !hasRequiredClassification(shift)) {
    return 'CREDENTIAL_REQUIREMENT_ABSENT';
  }

  return 'OPERATIONAL';
}

// ── Public entry point ─────────────────────────────────────────────────────

/**
 * Rank unfilled shifts by regulatory / operational consequence.
 *
 * Lapsed shifts are split out first via `classifyUnfilledUrgency` and never
 * appear in `ranked`. In-progress and upcoming shifts are both work-list
 * items — mid-shift is still fillable for the remaining hours.
 */
export function rankUnfilledByConsequence<T extends UnfilledShiftForConsequence>(
  input: RankUnfilledByConsequenceInput<T>,
): RankUnfilledByConsequenceResult<T> {
  const urgencyCounts = countUnfilledByUrgency(input.unfilledShifts, input.nowMs);
  const rnGapHourStarts = collectRnGapHourStarts(input.rnCoverDays, input.assignedRnIntervals);
  const underTargetDates = underCareMinuteTargetDates(
    input.careMinuteTargetResolution,
    input.careMinuteDays,
  );

  const lapsed: T[] = [];
  const ranked: RankedUnfilledShift<T>[] = [];

  for (const shift of input.unfilledShifts) {
    const urgency = classifyUnfilledUrgency({
      startMs: shift.startMs,
      endMs: shift.endMs,
      nowMs: input.nowMs,
    });
    if (urgency === 'LAPSED') {
      lapsed.push(shift);
      continue;
    }

    const rank = classifyConsequenceRank(shift, rnGapHourStarts, underTargetDates);
    ranked.push({
      shift,
      rank,
      reason: unfilledConsequenceReason(rank),
      urgency,
    });
  }

  ranked.sort((a, b) => {
    const orderDelta =
      UNFILLED_CONSEQUENCE_RANK_ORDER[a.rank] - UNFILLED_CONSEQUENCE_RANK_ORDER[b.rank];
    if (orderDelta !== 0) return orderDelta;
    // Within a rank, earlier starts first so the board remains scannable.
    if (a.shift.startMs !== b.shift.startMs) return a.shift.startMs - b.shift.startMs;
    // Stable fallback for equal starts.
    return a.shift.id < b.shift.id ? -1 : a.shift.id > b.shift.id ? 1 : 0;
  });

  let breaksRnCover = 0;
  let underCareMinuteTarget = 0;
  let credentialRequirementAbsent = 0;
  let operational = 0;
  for (const item of ranked) {
    switch (item.rank) {
      case 'BREAKS_RN_COVER':
        breaksRnCover += 1;
        break;
      case 'UNDER_CARE_MINUTE_TARGET':
        underCareMinuteTarget += 1;
        break;
      case 'CREDENTIAL_REQUIREMENT_ABSENT':
        credentialRequirementAbsent += 1;
        break;
      case 'OPERATIONAL':
        operational += 1;
        break;
    }
  }

  const countsByRank: UnfilledConsequenceCounts = {
    breaksRnCover,
    underCareMinuteTarget,
    credentialRequirementAbsent,
    operational,
  };

  /*
     Ranks 1-2 only, deliberately.

     Grok flagged the original 1-3 as slightly strong and it was right. Breaking
     RN cover and pushing a day under its care-minute target are regulatory
     consequences: one is a condition of operating, the other drives funding and
     an externally audited statement. A missing classification requirement is a
     DATA-QUALITY problem — it makes the shift unsafe to fill by matching, which
     is why it still sorts above operational work, but it is not itself a breach.

     Counting it under a red "affecting compliance" chip would inflate that
     number with rows that break no rule. At Medea the credential gap is on 73%
     of shifts, so it would have dominated the chip permanently and taught a
     coordinator to ignore it — which is exactly how a real RN cover break gets
     missed.
  */
  const affectingComplianceCount = breaksRnCover + underCareMinuteTarget;

  return {
    ranked,
    lapsed,
    urgencyCounts,
    countsByRank,
    affectingComplianceCount,
  };
}
